import { observer } from 'mobx-react'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import Spinner from '../components/Spinner'
import { useParams, useSearchParams } from 'react-router-dom'
import BreadCrumbs from '../components/Breadcrumbs'
import { Container } from '../components/Animations'
import Radom, { Customer, Payout as IPayout, ManagedBalanceLedger } from '../state/Radom'
import { StatusBadge } from './CheckoutSessions'
import _ from 'lodash'
import {
  errorToast,
  formatAssetAmount,
  shortAddress,
  successToast,
  unifyLedger,
  useTestMode,
  ROUTES
} from '../util/Util'
import TimeText from '../components/TimeText'
import Table from '../components/Table'
import { IconButton, SecondaryLinkButton } from '../components/Button'
import Copy from '../icons/Copy'
import { ExternalLinkText } from './Events'
import { formatExplorerUrl } from '../util/Chains'
import LinkExternal from '../components/LinkExternal'
import { getMethod, getMethodLogoByTicker, ManagedPaymentMethod, prodNetworks, testNetworks } from '../util/Managed'
import { payoutBadgeStatus, payoutStatusLabel } from '../util/PayoutStatus'
import { Section, SectionContent, SectionItem, SubTitle } from '../components/Shared'
import Modal from '../components/Modal'
import ExportPayoutEntriesForm from '../components/ExportPayoutEntriesForm'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import Dots from '../icons/Dots'
import Gate from '../components/Gate'
import { usePermissions } from '../util/permissions'
import HedgedPayoutCostBreakdown from './Funds/PayoutsComponents/HedgedPayoutCostBreakdown'
import {
  linkExternalIconStyle,
  loadingWrapperStyle,
  containerStyle,
  headerRowStyle,
  dropdownContentStyle,
  payoutHeaderStyle,
  payoutLogoStyle,
  tickerBadgeStyle,
  feeRowStyle,
  feeLogoStyle,
  ledgerTitleStyle,
  ledgerRowStyle,
  ledgerMutedTextStyle,
  withdrawalTitleStyle,
  payoutAddressCellStyle,
  iconButtonNoPaddingStyle,
  copyIconStyle,
  txLinkStyle
} from './PayoutStyles'

const MassivePayout = observer((): ReactElement => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const source = searchParams.get('source')
  const { has, isLoaded } = usePermissions()

  const [payout, setPayout] = useState<IPayout>()
  const [isLoading, setIsLoading] = useState(true)
  const [paymentMethod, setPaymentMethod] = useState<ManagedPaymentMethod>()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [managedBalanceLedger, setManagedBalanceLedger] = useState<ManagedBalanceLedger[]>()
  const [exportPayoutEntriesModalOpen, setExportPayoutEntriesModalOpen] = useState(false)
  const [testMode] = useTestMode()

  const copyToClipboard = useCallback(
    async (text: string, successMsg: string, failureMsg: string) => {
      try {
        await navigator.clipboard.writeText(text)
        successToast(successMsg)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('copy failed', err)
        errorToast(failureMsg)
      }
    },
    []
  )

  const canReadEvents = has('ReadEvents')
  const canReadCustomers = has('ReadCustomers')

  const fetchPayoutData = useCallback(
    async (id: string): Promise<void> => {
      setIsLoading(true)

      let payoutResp: IPayout
      try {
        payoutResp = await Radom.getPayout(id)
        setPayout(payoutResp)

        if (payoutResp.cryptoEntries && payoutResp.cryptoEntries.length > 0) {
          const firstCrypto = payoutResp.cryptoEntries[0]
          setPaymentMethod(getMethod(firstCrypto.network, firstCrypto.token))
        } else if (payoutResp.payouts?.[0]?.payoutType?.crypto) {
          setPaymentMethod(
            getMethod(
              payoutResp.payouts[0].payoutType.crypto.network,
              payoutResp.payouts[0].payoutType.crypto.token
            )
          )
        }
      } catch {
        setIsLoading(false)
        errorToast('Failed to load payout')
        return
      }

      if (canReadEvents) {
        try {
          const ledger = await Radom.listManagedBalanceLedger([payoutResp.id])
          if (ledger.length === 1) {
            setManagedBalanceLedger(ledger)
          } else {
            setManagedBalanceLedger(unifyLedger(ledger))
          }
        } catch {
          errorToast('Failed to load ledger')
        }
      }

      if (!canReadCustomers) {
        setIsLoading(false)
        return
      }

      // Collect customer IDs from crypto entries (new format) or legacy payouts
      const customerIdSet = new Set<string>()
      if (payoutResp.cryptoEntries) {
        for (const entry of payoutResp.cryptoEntries) {
          if (entry.customerId) customerIdSet.add(entry.customerId)
        }
      }
      if (payoutResp.fiatEntries) {
        for (const entry of payoutResp.fiatEntries) {
          if (entry.customerId) customerIdSet.add(entry.customerId)
        }
      }
      if (payoutResp.payouts) {
        for (const entry of payoutResp.payouts) {
          if (entry.payoutType.crypto?.customerId) customerIdSet.add(entry.payoutType.crypto.customerId)
        }
      }
      const ids = Array.from(customerIdSet)

      const needed = new Set(ids)
      const found: Customer[] = []
      const limit = 20
      const parallelQueries = 3
      const networks = testMode ? testNetworks : prodNetworks
      const sortType = 'createdAtDescending'

      try {
        const firstPage = await Radom.listCustomers(0, limit, networks, sortType)
        const total = firstPage.total

        for (const c of firstPage.data) {
          if (needed.has(c.id)) {
            found.push(c)
            needed.delete(c.id)
            if (needed.size === 0) break
          }
        }

        if (total >= limit && needed.size > 0) {
          for (
            let baseOffset = limit;
            baseOffset < total && needed.size > 0;
            baseOffset += limit * parallelQueries
          ) {
            const offsets: number[] = []
            for (let k = 0; k < parallelQueries; k++) {
              const off = baseOffset + k * limit
              if (off < total) offsets.push(off)
            }

            const pages = await Promise.all(
              offsets.map(async off => await Radom.listCustomers(off, limit, networks, sortType))
            )

            for (const page of pages) {
              for (const c of page.data) {
                if (needed.has(c.id)) {
                  found.push(c)
                  needed.delete(c.id)
                  if (needed.size === 0) break
                }
              }
              if (needed.size === 0) break
            }
          }
        }

        setCustomers(found)
      } catch {
        errorToast('Failed to load contacts')
      }

      setIsLoading(false)
    },
    [testMode, canReadEvents, canReadCustomers]
  )

  const debouncedFetchPayoutData = useMemo(() => _.debounce(fetchPayoutData, 300), [fetchPayoutData])

  const handleCloseExportPayoutEntriesModal = useCallback(() => {
    setExportPayoutEntriesModalOpen(false)
  }, [])

  const handleOpenExportPayoutEntriesModal = useCallback(() => {
    setExportPayoutEntriesModalOpen(true)
  }, [])

  useEffect(() => {
    if (!params.id) return

    if (!isLoaded) return
    debouncedFetchPayoutData(params.id)
    return () => debouncedFetchPayoutData.cancel()
  }, [params.id, debouncedFetchPayoutData, isLoaded])

  if (isLoading) {
    return (
      <div style={loadingWrapperStyle}>
        <Spinner />
      </div>
    )
  }

  if (payout) {
    const isFiat =
      payout.fiatEntries &&
      payout.fiatEntries.length > 0 &&
      (!payout.cryptoEntries || payout.cryptoEntries.length === 0)
    const displayTicker = isFiat ? (payout.fiatEntries[0]?.currency || 'USD') : (paymentMethod?.ticker ?? 'USDC')

    const nativeTotal = isFiat
      ? payout.fiatEntries.reduce((sum, e) => sum + Number(e.amount || 0), 0)
      : (payout.cryptoEntries ?? []).reduce((sum, e) => sum + Number(e.amount || 0), 0)
    const displayTotal = nativeTotal > 0
      ? String(nativeTotal)
      : (payout.totalAmountUsd || payout.totalPayoutUsd)

    return (
      <Container style={containerStyle}>
        <div style={headerRowStyle}>
          <BreadCrumbs
            items={
              source === 'transactions'
                ? [
                  { item: 'Transactions', to: '/transactions' },
                  { item: payout.id, to: `${ROUTES.getMassivePayoutDetailRoute(payout.id)}?source=transactions` }
                ]
                : [
                  { item: 'Mass payouts', to: ROUTES.MASSIVE_PAYOUTS },
                  { item: payout.id, to: '.' }
                ]
            }
          />

          <Gate permission='ReadEvents'>
            <Dropdown
              noChevron
              iconTrigger
              selectedContent={<Dots width={15} />}
              dropdownContent={
                <div style={dropdownContentStyle}>
                  <DropdownItem onClick={handleOpenExportPayoutEntriesModal}>
                    Export payout entries
                  </DropdownItem>
                </div>
              }
            />
          </Gate>
        </div>

        <Section>
          <div style={payoutHeaderStyle}>
            {paymentMethod && <img src={paymentMethod.logo} style={payoutLogoStyle} />}
            <span>{formatAssetAmount(displayTotal, displayTicker, 6)}</span>
            <span style={tickerBadgeStyle}>{displayTicker}</span>
          </div>

          <SectionContent>
            <SectionItem>
              <SubTitle>{isFiat ? 'Fee' : (payout.cryptoEntries ?? []).some(e => e.hedge) ? 'Radom fee' : 'Network fee'}</SubTitle>
              <div style={feeRowStyle}>
                {(() => {
                  const fundingCurrency = payout.cryptoEntries?.[0]?.fundingCurrency
                  const feeTicker = isFiat ? 'USDC' : (fundingCurrency || 'USD')
                  const feeLogo = getMethodLogoByTicker(feeTicker)
                  return <>
                    {feeLogo && <img src={feeLogo} style={feeLogoStyle} />}
                    <span>{formatAssetAmount(payout.estimatedFeeUsd, undefined, 6)} {feeTicker}</span>
                  </>
                })()}
              </div>
            </SectionItem>

            <SectionItem>
              <SubTitle>Status</SubTitle>
              <StatusBadge status={payoutBadgeStatus(payout.status)}>
                {payoutStatusLabel(payout.status, payout.autoConfirm)}
              </StatusBadge>
            </SectionItem>

            <SectionItem>
              <SubTitle>Deduct fees</SubTitle>
              <span>{payout.deductFees ? 'Yes' : 'No'}</span>
            </SectionItem>

            <SectionItem>
              <SubTitle>Email receipt</SubTitle>
              <span>{payout.sendEmailReceipts ? 'Yes' : 'No'}</span>
            </SectionItem>

            <SectionItem>
              <SubTitle>Created</SubTitle>
              <TimeText date={new Date(payout.createdAt)} />
            </SectionItem>

            <SectionItem>
              <SubTitle>Completed</SubTitle>
              {payout.completedAt
                ? <TimeText date={new Date(payout.completedAt)} />
                : <span>Not yet</span>}
            </SectionItem>
          </SectionContent>
        </Section>

        {(payout.cryptoEntries ?? []).some(entry => entry.hedge) && (
          <Section>
            <SubTitle style={ledgerTitleStyle}>Conversion details</SubTitle>

            {(payout.cryptoEntries ?? []).filter(entry => entry.hedge).map(entry => {
              const hedge = entry.hedge
              if (!hedge) return null
              const cryptoTicker = getMethod(entry.network, entry.token).ticker

              return (
                <div key={entry.id} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {(payout.cryptoEntries?.length ?? 0) > 1 && (
                    <span style={ledgerMutedTextStyle}>
                      Entry to {shortAddress(entry.payoutAddress, 8)}
                    </span>
                  )}

                  <SectionContent>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <HedgedPayoutCostBreakdown
                        hedge={hedge}
                        cryptoTicker={cryptoTicker}
                        recipientAmount={entry.amount}
                      />
                    </div>

                    <SectionItem>
                      <SubTitle>Delivery withdrawal</SubTitle>
                      {hedge.deliveryWithdrawalId
                        ? <SecondaryLinkButton
                          to={`/withdrawal/${hedge.deliveryWithdrawalId}`}
                          style={{ minHeight: 32, padding: '0 12px', fontSize: 13 }}
                        >
                          View withdrawal
                        </SecondaryLinkButton>
                        : <span>-</span>}
                    </SectionItem>
                  </SectionContent>
                </div>
              )
            })}
          </Section>
        )}

        {managedBalanceLedger && managedBalanceLedger.length > 0 && (
          <Section>
            <SubTitle style={ledgerTitleStyle}>Ledger</SubTitle>

            {managedBalanceLedger.map((item, idx) => {
              const method = getMethod(item.network, item.token)

              return (
                <SectionContent key={idx}>
                  <SectionItem>
                    <SubTitle>Balance Before</SubTitle>
                    <div style={ledgerRowStyle}>
                      <span>{formatAssetAmount(item.balanceBefore, method.ticker)}</span>
                      <span style={ledgerMutedTextStyle}>
                        {method.ticker ?? method.logo} on {item.network}
                      </span>
                    </div>
                  </SectionItem>

                  <SectionItem>
                    <SubTitle>Balance After</SubTitle>
                    <div style={ledgerRowStyle}>
                      <span>{formatAssetAmount(item.balanceAfter, method.ticker)}</span>
                      <span style={ledgerMutedTextStyle}>
                        {method.ticker ?? method.logo} on {item.network}
                      </span>
                    </div>
                  </SectionItem>
                </SectionContent>
              )
            })}
          </Section>
        )}

        {/* Crypto entries table */}
        {payout.cryptoEntries && payout.cryptoEntries.length > 0 && paymentMethod && (
          <Section>
            <SubTitle style={withdrawalTitleStyle}>Crypto payout entries</SubTitle>

            <Table
              disableHoverStyle
              headers={[
                <span key="address">Payout address</span>,
                <span key="amount">Amount</span>,
                <span key="network">Network</span>,
                <span key="name">Beneficiary Name</span>,
                <span key="status">Status</span>,
                ...(payout.cryptoEntries.some(e => e.failureReason)
                  ? [<span key="reason">Reason</span>]
                  : []),
                <span key="hash">Transaction Hash</span>
              ]}
              items={payout.cryptoEntries.map((entry, i) => {
                const customer = customers.find(c => c.id === entry.customerId)
                const hasName = Boolean(customer?.name && customer.name.length !== 0 && customer.name !== 'null null')
                const txHash = entry.txHash ?? ''
                const hasReasonCol = payout.cryptoEntries.some(e => e.failureReason)

                return [
                  <div key={`${i}-a`} style={payoutAddressCellStyle}>
                    <span>{shortAddress(entry.payoutAddress, 8)}</span>
                    <IconButton style={iconButtonNoPaddingStyle}>
                      <Copy
                        style={copyIconStyle}
                        onClick={e => {
                          e.stopPropagation()
                          void copyToClipboard(entry.payoutAddress, 'Copied payout address', 'Failed to copy payout address')
                        }}
                      />
                    </IconButton>
                  </div>,

                  <div key={`${i}-b`}>
                    <div style={feeRowStyle}>
                      <img src={paymentMethod.logo} style={feeLogoStyle} />
                      <span>{formatAssetAmount(entry.amount, paymentMethod.ticker)}</span>
                    </div>
                  </div>,

                  <div key={`${i}-c`}>
                    <div style={feeRowStyle}>
                      <img
                        src={paymentMethod.hostChain?.logo ?? paymentMethod.logo}
                        style={feeLogoStyle}
                      />
                      <span>{paymentMethod.hostChain?.name ?? paymentMethod.name}</span>
                    </div>
                  </div>,

                  <div key={`${i}-d`}>
                    <span>{hasName ? customer?.name : '-'}</span>
                  </div>,

                  <div key={`${i}-s`}>
                    <span>{payoutStatusLabel(entry.status)}</span>
                  </div>,

                  ...(hasReasonCol
                    ? [<div key={`${i}-reason`}>
                      <span>{entry.failureReason || '-'}</span>
                    </div>]
                    : []),

                  <div key={`${i}-f`}>
                    {txHash
                      ? <ExternalLinkText
                        style={txLinkStyle}
                        onClick={e => e.stopPropagation()}
                        target="_blank"
                        rel="noreferrer"
                        href={formatExplorerUrl(entry.network, 'tx', txHash)}
                      >
                        {shortAddress(txHash, 8)}
                        <LinkExternal stroke={linkExternalIconStyle.stroke} width={linkExternalIconStyle.width} />
                      </ExternalLinkText>
                      : <span>-</span>
                    }
                  </div>
                ]
              })}
              isLoading={false}
            />
          </Section>
        )}

        {/* Fiat entries table */}
        {payout.fiatEntries && payout.fiatEntries.length > 0 && (
          <Section>
            <SubTitle style={withdrawalTitleStyle}>Fiat payout entries</SubTitle>

            <Table
              disableHoverStyle
              headers={[
                <span key="name">Beneficiary</span>,
                <span key="amount">Amount</span>,
                <span key="currency">Currency</span>,
                <span key="status">Status</span>,
                ...(payout.fiatEntries.some(e => e.failureReason)
                  ? [<span key="reason">Reason</span>]
                  : []),
                <span key="ref">Provider Reference</span>
              ]}
              items={payout.fiatEntries.map((entry, i) => {
                const hasReasonCol = payout.fiatEntries.some(e => e.failureReason)
                return [
                  <div key={`${i}-n`}>
                    <span>{entry.beneficiaryName || '-'}</span>
                  </div>,
                  <div key={`${i}-a`}>
                    <span>{formatAssetAmount(entry.amount, entry.currency)}</span>
                  </div>,
                  <div key={`${i}-c`}>
                    <span>{entry.currency}</span>
                  </div>,
                  <div key={`${i}-s`}>
                    <span>{payoutStatusLabel(entry.status)}</span>
                  </div>,
                  ...(hasReasonCol
                    ? [<div key={`${i}-reason`}>
                      <span>{entry.failureReason || '-'}</span>
                    </div>]
                    : []),
                  <div key={`${i}-r`}>
                    <span>{entry.providerReference ? shortAddress(entry.providerReference, 8) : '-'}</span>
                  </div>
                ]
              })}
              isLoading={false}
            />
          </Section>
        )}

        {exportPayoutEntriesModalOpen && (
          <Modal
            visible={exportPayoutEntriesModalOpen}
            onClose={handleCloseExportPayoutEntriesModal}
            title="Export payout entries"
          >
            <ExportPayoutEntriesForm id={payout.id} />
          </Modal>
        )}
      </Container>
    )
  }

  return (
    <div>
      <span>Mass Payout not found</span>
    </div>
  )
})

export default MassivePayout
